/**
 * Identity Token Bridge Component
 * 
 * This component bridges the @workspace/identity-react authentication with the
 * existing feedbackApi token requirements. It sets up the global token provider
 * when identity is available and clears it when identity becomes unavailable.
 * 
 * This component should be rendered inside the IdentityProviderWrapper and
 * wraps the main application content.
 */

import React, { useEffect, useCallback } from 'react';
import { useAppIdentity } from '../hooks/useAppIdentity';
import { setTokenProvider, clearTokenProvider } from '../services/identityTokenProvider';
import { loadOIDCState } from '../utils/oidcStateManager';

interface IdentityTokenBridgeProps {
  children: React.ReactNode;
}

/**
 * IdentityTokenBridge component
 * 
 * Connects the identity authentication system with the feedbackApi token provider.
 * When the user is authenticated via @workspace/identity-react, this component
 * sets up the token provider so that API calls can use the OIDC access token.
 */
export const IdentityTokenBridge: React.FC<IdentityTokenBridgeProps> = ({ children }) => {
  const { isAuthenticated, isLoading, isError, error, getAccessToken, requestReauthentication } = useAppIdentity();
  
  // Create a stable token provider function
  const tokenProvider = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (err) {
      console.error('Error getting access token:', err);
      // If we get a login_required error, request re-authentication
      if (err instanceof Error && err.message === 'login_required') {
        requestReauthentication();
      }
      return null;
    }
  }, [getAccessToken, requestReauthentication]);
  
  // Initialise token provider early if we have saved OIDC state
  useEffect(() => {
    const savedState = loadOIDCState();
    if (savedState?.isAuthenticated && savedState.accessToken && !isAuthenticated && !isLoading) {
      // Set a basic token provider that can use saved state until identity provider loads
      const fallbackProvider = async () => {
        const currentSavedState = loadOIDCState();
        return currentSavedState?.accessToken || null;
      };
      setTokenProvider(fallbackProvider);
    }
  }, [isAuthenticated, isLoading]); // Only run once on mount
  
  // Set up the token provider when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading && !isError) {
      setTokenProvider(tokenProvider);
      
      // Test the token provider immediately to verify it works
      tokenProvider().then(token => {
        if (!token) {
          console.warn('IdentityTokenBridge: Token provider test failed - no token returned');
        }
      }).catch(error => {
        console.error('IdentityTokenBridge: Token provider test failed:', error);
      });
    } else {
      // Only clear token provider if we don't have saved state
      const savedState = loadOIDCState();
      if (!savedState?.isAuthenticated) {
        clearTokenProvider();
      }
    }
    
    // Cleanup on unmount
    return () => {
      clearTokenProvider();
    };
  }, [isAuthenticated, isLoading, isError, tokenProvider, getAccessToken]);
  
  // Log any authentication errors
  useEffect(() => {
    if (isError && error) {
      console.error('IdentityTokenBridge: Authentication error:', error);
    }
  }, [isError, error]);
  
  return <>{children}</>;
};

export default IdentityTokenBridge;
